import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success('Konto utworzone!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad rejestracji');
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="dark min-h-screen flex bg-background">
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,hsl(189_70%_45%/0.18),transparent_55%),radial-gradient(700px_circle_at_80%_0%,hsl(206_70%_55%/0.12),transparent_50%)]" />
        <div className="absolute inset-0 bg-card/50" />
        <div className="relative z-10 px-12 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <span className="font-heading font-bold text-3xl tracking-tight">Torr.pl</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold mb-4">Dolacz do platformy</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Zarejestruj sie jako afiliant i zacznij zarabiac prowizje od kampanii SMS i email.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-border/50 bg-card/80 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-primary" />
              <span className="font-heading font-bold text-xl">Torr.pl</span>
            </div>
            <CardTitle className="font-heading text-xl">Rejestracja</CardTitle>
            <CardDescription>Utworz konto afilianta</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Imie</Label>
                  <Input value={form.first_name} onChange={update('first_name')} required data-testid="register-first-name-input" className="bg-background/50" />
                </div>
                <div className="space-y-2">
                  <Label>Nazwisko</Label>
                  <Input value={form.last_name} onChange={update('last_name')} required data-testid="register-last-name-input" className="bg-background/50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={update('email')} required data-testid="register-email-input" className="bg-background/50" />
              </div>
              <div className="space-y-2">
                <Label>Haslo</Label>
                <Input type="password" value={form.password} onChange={update('password')} required minLength={6} data-testid="register-password-input" className="bg-background/50" placeholder="Min. 6 znakow" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Zarejestruj
              </Button>
            </form>
            <Separator className="my-4" />
            <p className="text-center text-sm text-muted-foreground">
              Masz juz konto?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">Zaloguj sie</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
